import * as React from 'react'
import { noteForeground, notePalette, type NotePaletteId } from '../../shared/note-palettes'

export type Theme = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'family-notes:theme'
const PALETTE_STORAGE_KEY = 'family-notes:note-palette'
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

function storedTheme(): Theme {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

function systemIsDark() {
  return typeof matchMedia === 'function' && matchMedia(SYSTEM_DARK_QUERY).matches
}

function storedPalette(): NotePaletteId {
  try { return notePalette(localStorage.getItem(PALETTE_STORAGE_KEY)).id } catch { return 'dynamic' }
}

const ThemeContext = React.createContext<{
  theme: Theme; setTheme: (theme: Theme) => void
  notePaletteId: NotePaletteId; setNotePalette: (palette: NotePaletteId) => void
} | null>(null)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>(storedTheme)
  const [notePaletteId, setPaletteState] = React.useState<NotePaletteId>(storedPalette)
  const [systemDark, setSystemDark] = React.useState(systemIsDark)

  React.useEffect(() => {
    if (typeof matchMedia !== 'function') return
    const media = matchMedia(SYSTEM_DARK_QUERY)
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  React.useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY || event.key === null) setThemeState(storedTheme())
      if (event.key === PALETTE_STORAGE_KEY || event.key === null) setPaletteState(storedPalette())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  React.useLayoutEffect(() => {
    const dark = theme === 'dark' || (theme === 'system' && systemDark)
    document.documentElement.classList.toggle('dark', dark)
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (themeColor) themeColor.content = (dark ? themeColor.dataset.dark : themeColor.dataset.light) ?? themeColor.content
    const colorScheme = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]')
    if (colorScheme) colorScheme.content = dark ? 'dark' : 'light'
  }, [theme, systemDark])

  React.useLayoutEffect(() => {
    document.documentElement.dataset.notePalette = notePaletteId
    for (const [color, shades] of Object.entries(notePalette(notePaletteId).colors)) {
      document.documentElement.style.setProperty(`--note-palette-${color}-light`, shades.light)
      document.documentElement.style.setProperty(`--note-palette-${color}-dark`, shades.dark)
      document.documentElement.style.setProperty(`--note-palette-${color}-on-light`, notePaletteId === 'dynamic' ? '#242424' : noteForeground(shades.light))
      document.documentElement.style.setProperty(`--note-palette-${color}-on-dark`, notePaletteId === 'dynamic' ? '#f4f4f4' : noteForeground(shades.dark))
    }
  }, [notePaletteId])

  const setTheme = React.useCallback((next: Theme) => {
    setThemeState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* Keep the choice for this tab when storage is unavailable. */ }
  }, [])

  const setNotePalette = React.useCallback((next: NotePaletteId) => {
    const valid = notePalette(next).id
    setPaletteState(valid)
    try { localStorage.setItem(PALETTE_STORAGE_KEY, valid) } catch { /* Keep this tab's choice. */ }
  }, [])

  return <ThemeContext.Provider value={{ theme, setTheme, notePaletteId, setNotePalette }}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = React.useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside ThemeProvider')
  return context
}
